<template>
  <v-container fluid>
    <v-btn variant="outlined" @click="refreshTree" v-if="false">REFRESH</v-btn>
    <v-treeview
      density="compact"
      :items="virtualRootMenuItem.children"
      :load-children="updateMenuChildren"
      v-model:opened="menuItemOpen"
      item-value="id"
      transition
      return-object
      open-on-click
      activatable
      ref="menuTree"
      @update:activated="onSelectedNode"
    >
      <template v-slot:prepend="{ item, isOpen }">
        <v-icon size="small" v-if="MenuUtils.isTableContent(item)">mdi-table</v-icon>
        <v-icon size="small" v-if="MenuUtils.isCardsContent(item)">mdi-view-grid-outline</v-icon>
        <v-icon size="small" v-if="MenuUtils.isTreeMenu(item)">
          {{ isOpen ? 'mdi-folder-open' : 'mdi-folder' }}
        </v-icon>
        <v-icon size="small" v-if="item.actionType === MenuActionType.CORTONA_MODEL">mdi-rotate-3d</v-icon>
        <v-icon size="small" v-if="item.actionType === MenuActionType.FILE">{{ fileIcon(item.mimeType) }}</v-icon>
      </template>
      <template v-slot:title="{ item }">
        <span v-if="MenuUtils.isTreeMenu(item)">{{ item.name }}</span>
        <span v-if="MenuUtils.shouldOpenInMainArea(item)">{{ item.name }}</span>
        <span v-if="MenuUtils.shouldShowAsDialogWithContent(item)">{{ item.name }}</span>
      </template>
      <template v-slot:append="{ item }">
        <span>
          <v-btn
            @click.stop="addNewMenuItem(item)"
            icon
            size="small"
            variant="text"
            v-if="isAdmin && MenuUtils.hasEditableChildren(item)"
            title="Add a child menu"
          >
            <v-icon size="small">mdi-plus</v-icon>
          </v-btn>
          <v-btn @click.stop="loadMenuAndShowEditDialog(item)" icon size="small" variant="text" v-if="isAdmin && MenuUtils.isEditableMenu(item)">
            <v-icon size="small">mdi-pencil</v-icon>
          </v-btn>
          <v-btn @click.stop="cloneMenu(item)" icon size="small" variant="text" v-if="isAdmin && MenuUtils.isEditableMenu(item)" :title="$t('admin-menus.tree.clone')">
            <v-icon size="small">mdi-content-copy</v-icon>
          </v-btn>
          <v-btn @click.stop="deleteMenu(item)" icon size="small" variant="text" v-if="isAdmin && MenuUtils.isEditableMenu(item)" title="Delete this menu">
            <v-icon size="small">mdi-delete</v-icon>
          </v-btn>
        </span>
      </template>
    </v-treeview>

    <!-- dialog z edycją wybranego elementu -->
    <menu-edit-dialog
      v-if="isAdmin"
      :editDialog="editDialog"
      :selectedMenuItem="editedItem"
      @closeEditMenuItem="onCloseMenuItemEditDialog"
    />
    <confirm-dialog ref="confirmDialog" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue';
import type { MenuId, MenuItemValue } from '@/venit/services/model/techpubs-cms';
import { ApiMenuReadMenuItemDto, MenuActionType, MenuContentLayout } from '@/venit/services/model/techpubs-cms';
import ApiMenuService from '@/venit/services/api/menu/menu.service';
import ConfirmDialog from '@/venit/components/confirm-dialog/confirm-dialog.vue';
import MenuManagementService from '@/venit/admin/menu-management/menu-management.service';
import AccountService from '@/account/account.service';
import MenuEditDialog from '@/venit/admin/menu-management/menu-management-edit.vue';
import MenuUtils from '@/shared/menu/menu-utils.service';
import { useTranslationStore, useAccountStore } from '@/stores';

const props = defineProps<{
  rootMenuId: MenuId;
}>();

const emit = defineEmits<{
  openMenuContent: [menuId: MenuId | null];
  openResourcePreview: [item: ApiMenuReadMenuItemDto];
}>();

    const menuManagementService = inject<() => MenuManagementService>('menuManagementService')!;
    const menuService = inject<() => ApiMenuService>('menuService')!;
    const accountService = inject<() => AccountService>('accountService')!;

    const translationStore = useTranslationStore();
    const accountStore = useAccountStore();

    const confirmDialog = ref<InstanceType<typeof ConfirmDialog> | null>(null);
    const menuTree = ref<any>(null);

    // Helper functions
    const fileIcon = (mimeType: string | null | undefined): string => {
      if (mimeType?.startsWith('image/')) return 'mdi-file-image-outline';
      if (mimeType === 'application/pdf') return 'mdi-file-pdf-box';
      return 'mdi-file-outline';
    };

    const createVirtualRootMenu = (): ApiMenuReadMenuItemDto => ({
      id: -1,
      name: 'Root menu',
      children: [],
      actionType: MenuActionType.MENU_TEMPLATE_ROOT,
      parentId: null,
      contentLayout: MenuContentLayout.TREE,
      active: true,
      isLeaf: false,
      pathItems: [],
      parentUrlId: null,
      resourceId: null,
      sortNo: 0,
      url: null,
      thumbnailUrl: null,
      totalChildElements: null,
      urlId: null,
      mimeType: null,
    });

    const createEmptyChildMenuItemValue = (): MenuItemValue => ({
      menuId: { id: null },
      version: null,
      urlId: null,
      parentId: null,
      parentUrlId: null,
      name: null,
      isLeaf: null,
      actionType: null,
      contentLayout: MenuContentLayout.CARDS,
      sortNo: 1,
      pathItems: [],
      children: [],
      menuFilters: [],
      active: false,
      thumbnail: {
        uuid: null,
      },
      resourceId: null,
      publicDefault: false,
      translations: [],
    });

    // Reactive state
    const editDialog = ref(false);
    const editedItem = ref<MenuItemValue>(createEmptyChildMenuItemValue());
    const menuItemOpen = ref<ApiMenuReadMenuItemDto[]>([]);
    const virtualRootMenuItem = ref<ApiMenuReadMenuItemDto>(createVirtualRootMenu());
    const menuParents = ref(new Map<number, ApiMenuReadMenuItemDto>());

    // Computed
    const currentLanguage = computed(() => translationStore.currentLanguage);
    const authenticated = computed(() => accountStore.authenticated);
    const isAdmin = computed(() => authenticated.value && accountService().userAuthorities.includes('ROLE_ADMIN'));

    // Methods
    const openMenuContent = (menuId: number | null): MenuId | null => {
      if (menuId == null) {
        emit('openMenuContent', null);
        return null;
      }
      const result = { id: menuId };
      emit('openMenuContent', result);
      return result;
    };

    const onSelectedNode = (selectedNodes: ApiMenuReadMenuItemDto[]) => {
      if (selectedNodes && selectedNodes.length == 1) {
        let oneNode = selectedNodes[0];
        if (MenuUtils.shouldOpenInMainArea(oneNode)) {
          openMenuContent(oneNode.id);
        } else if (MenuUtils.shouldShowAsDialogWithContent(oneNode)) {
          emit('openResourcePreview', oneNode);
        }
      }
    };

    const fixChildrenForTree = (menuItem: ApiMenuReadMenuItemDto) => {
      if (MenuUtils.isTreeMenu(menuItem)) {
        if (!menuItem.children && !menuItem.isLeaf) {
          menuItem.children = [];
        }
      } else {
        menuItem.children = null;
      }
    };

    const updateVirtualRootMenuChildren = async (menuId: MenuId) => {
      const response = isAdmin.value
        ? await menuService().readAdmin(menuId.id, currentLanguage.value, 1, 100)
        : await menuService().read(menuId.id, currentLanguage.value, 1, 100);

      virtualRootMenuItem.value.children.length = 0;
      menuItemOpen.value.length = 0;

      if (response.data.menuItem.id === 0) {
        virtualRootMenuItem.value.children.push(...response.data.menuItem.children);
      } else {
        virtualRootMenuItem.value.children.push(response.data.menuItem);
        response.data.menuItem.children.forEach(child => fixChildrenForTree(child));
      }

      virtualRootMenuItem.value.children.forEach(child => {
        fixChildrenForTree(child);
        menuParents.value.set(child.id, virtualRootMenuItem.value);
      });

      menuItemOpen.value.length = 0;
    };

    const updateMenuChildren = async (item: ApiMenuReadMenuItemDto) => {
      const response = isAdmin.value
        ? await menuService().readAdmin(item.id, currentLanguage.value, 1, 100)
        : await menuService().read(item.id, currentLanguage.value, 1, 100);

      const menu = response.data.menuItem;
      fixChildrenForTree(menu);
      menu.children.forEach(mi => {
        fixChildrenForTree(mi);
        item.children.push(mi);
        menuParents.value.set(mi.id, item);
      });
    };

    const refreshTree = async () => {
      menuItemOpen.value = [];
      await updateVirtualRootMenuChildren(props.rootMenuId);
      openMenuContent(null);
    };

    const deleteMenu = async (itemToRemove: ApiMenuReadMenuItemDto) => {
      if (await confirmDialog.value?.openWithMessage('Delete this item?')) {
        await menuManagementService().deleteMenu({ menuId: { id: itemToRemove.id } });
        await refreshTree();
      }
    };

    const cloneMenu = async (item: ApiMenuReadMenuItemDto) => {
      await menuManagementService().cloneMenu({ menuId: { id: item.id } });
      await refreshTree();
    };

    const loadMenuAndShowEditDialog = async (item: ApiMenuReadMenuItemDto) => {
      const menuValue: MenuItemValue = await menuManagementService().loadMenuValueById({ id: item.id });
      if (menuValue) {
        showEditItemDialog(menuValue);
      }
    };

    const addNewMenuItem = (parentMenu: ApiMenuReadMenuItemDto) => {
      const newMenuOption = createEmptyChildMenuItemValue();
      newMenuOption.parentId = { id: parentMenu.id };
      showEditItemDialog(newMenuOption);
    };

    const showEditItemDialog = (item: MenuItemValue) => {
      editedItem.value = item;
      editDialog.value = true;
    };

    const onCloseMenuItemEditDialog = async (saved: boolean) => {
      closeEditItemDialog();
      if (saved) {
        await refreshTree();
      }
    };

    const closeEditItemDialog = () => {
      editDialog.value = false;
    };

    // Lifecycle
    onMounted(async () => {
      await updateVirtualRootMenuChildren(props.rootMenuId);
    });

</script>
